import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Badge, Button, Card, Divider, Empty, Input, List, Modal, Spin, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, CloudOutlined, SendOutlined, UploadOutlined, ReloadOutlined,
  UserOutlined, CheckOutlined,
} from '@ant-design/icons';
import api from '../api/client';

const { Text, Title } = Typography;

/**
 * «Чат» — бесшовный клиент Nextcloud Talk (opp.spzvg.ru) внутри ТЕРРА-СУП.
 * Комнаты, сообщения, вложения и непрочитанные — через backend-шлюз
 * /chat/talk/*, работающий от имени пользователя (его app-password NC).
 * Обновление: polling (комнаты 20 с, открытая комната 12 с).
 */

const ROOMS_POLL_MS = 20000;
const MESSAGES_POLL_MS = 12000;

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Рендер текста сообщения с messageParameters (упоминания/файлы). */
function renderTalkText(msg) {
  const params = msg.parameters || {};
  let text = msg.message || '';
  if (msg.is_deleted) return <Text delete type="secondary">сообщение удалено</Text>;
  // {name} → содержимое параметра
  const parts = [];
  const re = /\{([a-zA-Z0-9]+)\}/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const p = params[m[1]];
    if (p && p.name) {
      if (p.type === 'user' || p.type === 'guest' || p.type === 'call') {
        parts.push(<Tag key={m.index} color="blue" style={{ margin: 0 }}>{p.name}</Tag>);
      } else if (p.type === 'file') {
        parts.push(<Tag key={m.index} color="cyan" style={{ margin: 0 }}>📎 {p.name}</Tag>);
      } else {
        parts.push(<Text key={m.index} strong>{p.name}</Text>);
      }
    } else {
      parts.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span>{parts.map((p, i) => <span key={i}>{p}{' '}</span>)}</span>;
}

export default function TalkPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState(null);
  const [roomsError, setRoomsError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false); // 401: креды протухли
  const [flowOpen, setFlowOpen] = useState(false);
  const [flowPolling, setFlowPolling] = useState(false);
  const [flowDone, setFlowDone] = useState(false);
  const [autoConnecting, setAutoConnecting] = useState(false);
  const flowPollUrlRef = useRef(null);
  const flowPollUrlsRef = useRef([]); // все недавние попытки
  const [current, setCurrent] = useState(null); // {token, name}
  const [me, setMe] = useState(null); // мой username NC (для выравнивания)
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const msgListRef = useRef(null);
  const lastKnownRef = useRef(null);
  const currentRef = useRef(null);
  currentRef.current = current;

  // ── Загрузка комнат ────────────────────────────────────────────────
  const loadRooms = useCallback(async (quiet = false) => {
    try {
      const res = await api.get('/chat/talk/rooms');
      setRooms(res.data.rooms || []);
      setMe(res.data.me || null);
      setNotConfigured(false);
      setNeedsReconnect(false);
      setRoomsError(null);
    } catch (e) {
      if (e.response?.status === 428) {
        setNotConfigured(true);
      } else if (e.response?.status === 409) {
        setNeedsReconnect(true); // app-password NC недействителен
      } else {
        setRoomsError(e.response?.data?.detail || 'Не удалось получить комнаты NC Talk');
      }
      if (!quiet) setRooms([]);
    }
  }, []);

  useEffect(() => {
    if (notConfigured || needsReconnect) return; // карточка переподключения — не долбим NC
    loadRooms();
    const t = setInterval(() => loadRooms(true), ROOMS_POLL_MS);
    return () => clearInterval(t);
  }, [loadRooms, notConfigured, needsReconnect]);

  // ── Сообщения открытой комнаты ─────────────────────────────────────
  const loadMessages = useCallback(async (token, incremental = false) => {
    if (!token) return;
    try {
      const params = incremental && lastKnownRef.current
        ? { last_known: lastKnownRef.current } : { limit: 100 };
      const res = await api.get(`/chat/talk/${encodeURIComponent(token)}/messages`, { params });
      const incoming = res.data.messages || [];
      setMe(res.data.me || me);
      if (incremental) {
        if (incoming.length > 0) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => String(m.id)));
            const fresh = incoming.filter((m) => !seen.has(String(m.id)));
            const merged = [...prev, ...fresh];
            // отметим прочитанным
            const lastId = fresh[fresh.length - 1]?.id;
            if (lastId) {
              api.post(`/chat/talk/${encodeURIComponent(token)}/read`, {
                last_read_message_id: String(lastId),
              }).catch(() => {});
            }
            return merged;
          });
        }
      } else {
        // NC отдаёт историю от НОВЫХ к старым — разворачиваем: новые внизу
        const ordered = [...incoming].reverse();
        setMessages(ordered);
        // incoming[0] — САМОЕ НОВОЕ сообщение (NC отдаёт новые первыми):
        // именно до него помечаем прочитанным, иначе комната «висит» с кружком
        const newestId = incoming[0]?.id ?? ordered[ordered.length - 1]?.id;
        if (newestId) {
          lastKnownRef.current = String(newestId);
          api.post(`/chat/talk/${encodeURIComponent(token)}/read`, {
            last_read_message_id: String(newestId),
          }).catch(() => {});
        }
      }
    } catch (e) {
      if (!incremental) message.error(e.response?.data?.detail || 'Ошибка загрузки сообщений');
    }
  }, []);

  // Открытие комнаты
  const openRoom = useCallback((room) => {
    setCurrent({ token: room.token, name: room.name, unread: room.unread_messages });
    setMessages([]);
    setMessagesLoading(true);
    lastKnownRef.current = null;
    loadMessages(room.token).finally(() => setMessagesLoading(false));
    setRooms((prev) => (prev || []).map((r) => r.token === room.token ? { ...r, unread_messages: 0 } : r));
  }, [loadMessages]);

  // Polling открытой комнаты
  useEffect(() => {
    if (!current) return;
    const t = setInterval(() => {
      if (currentRef.current) loadMessages(currentRef.current.token, true);
    }, MESSAGES_POLL_MS);
    return () => clearInterval(t);
  }, [current, loadMessages]);

  // Автоскролл вниз
  useEffect(() => {
    if (msgListRef.current) msgListRef.current.scrollTop = msgListRef.current.scrollHeight;
  }, [messages]);

  // ── Отправка ───────────────────────────────────────────────────────
  const send = async () => {
    const t = text.trim();
    if (!t || !current || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/talk/${encodeURIComponent(current.token)}/messages`, { message: t });
      setText('');
      await loadMessages(current.token, true);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось отправить');
    } finally {
      setSending(false);
    }
  };

  const uploadFile = async (file) => {
    if (!current || fileUploading) return;
    setFileUploading(true);
    const hide = message.loading('Загрузка файла в Nextcloud…', 0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/chat/talk/${encodeURIComponent(current.token)}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadMessages(current.token, true);
      message.success('Файл отправлен');
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось отправить файл');
    } finally {
      hide();
      setFileUploading(false);
    }
  };

  // ── Авто-подключение: сервер сам создаёт app-password на NC ────────
  const autoConnect = async () => {
    setAutoConnecting(true);
    try {
      const res = await api.post('/exchange/nc-autoconnect');
      message.success(
        res.data.already
          ? `Уже подключено (${res.data.nc_username}) — загружаем комнаты…`
          : `Nextcloud подключён (${res.data.nc_username}). Загружаем комнаты…`
      );
      setNotConfigured(false);
      setNeedsReconnect(false);
      await loadRooms();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось подключиться автоматически');
    } finally {
      setAutoConnecting(false);
    }
  };

  // ── Device-flow подключение NC (SSO) ────────────────────────────────
  const startDeviceFlow = async () => {
    setFlowDone(false);
    // ВАЖНО: окно открываем СИНХРОННО в момент клика (user gesture) —
    // иначе после await браузер блокирует попап, и пользователь видит
    // только крутящуюся кнопку
    const win = window.open('about:blank', '_blank', 'width=520,height=700');
    try {
      const res = await api.post('/exchange/device-flow/start');
      const url = res.data.sso_url || res.data.login_url;
      flowPollUrlRef.current = res.data.poll_url;
      flowPollUrlsRef.current = [...flowPollUrlsRef.current.slice(-4), res.data.poll_url];
      if (win) {
        win.location.href = url;
        win.focus();
      } else {
        // жёсткий блокировщик попапов — даём прямую ссылку
        Modal.warning({
          title: 'Окно заблокировано',
          content: (
            <p>
              Разрешите всплывающие окна для этого сайта и нажмите кнопку снова.
              Либо откройте страницу подтверждения вручную:{' '}
              <a href={url} target="_blank" rel="noreferrer">{url}</a>
            </p>
          ),
        });
        return;
      }
      setFlowOpen(true);
      setFlowPolling(true);
      // poll до подтверждения (максимум ~3 минуты)
      for (let i = 0; i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const p = await api.post('/exchange/device-flow/poll', {
            poll_url: flowPollUrlRef.current,
            poll_urls: flowPollUrlsRef.current,
          });
          if (p.data.status === 'ok') {
            setFlowDone(true);
            setFlowPolling(false);
            message.success(`Nextcloud подключён (${p.data.nc_username}). Загружаем комнаты…`);
            setNotConfigured(false);
            setNeedsReconnect(false);
            await loadRooms();
            return;
          }
        } catch (e2) {
          if (e2.response?.status === 400) { message.error('Сессия подключения истекла, начните заново'); break; }
        }
      }
      setFlowPolling(false);
      message.info('Не дождались подтверждения — проверьте открытое окно и попробуйте снова');
    } catch (e) {
      setFlowPolling(false);
      win?.close();
      message.error(e.response?.data?.detail || 'Не удалось начать подключение');
    }
  };

  // ── Экран «NC не подключён / требует переподключения» ──────────────
  if (notConfigured || needsReconnect) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Чат (Nextcloud Talk)</h1>
          <p className="page-subtitle">Общение организации</p>
        </div>
        <Card style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center' }}>
          <CloudOutlined style={{ fontSize: 48, color: '#0082c9' }} />
          <Title level={4} style={{ marginTop: 16 }}>
            {needsReconnect ? 'Переподключите аккаунт Nextcloud' : 'Подключите аккаунт Nextcloud'}
          </Title>
          <p style={{ color: '#64748b' }}>
            {needsReconnect
              ? 'Сохранённый ключ Nextcloud больше не действует (сервер переустанавливался). Получите новый — вход через корпоративный аккаунт.'
              : 'Чат работает через ваш личный аккаунт Nextcloud (opp.spzvg.ru). Подключение — одним кликом через корпоративный вход (SSO).'}
          </p>
          <Button type="primary" size="large" loading={autoConnecting} onClick={autoConnect}>
            ⚡ {needsReconnect ? 'Переподключить автоматически' : 'Подключить автоматически'}
          </Button>
          <p style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
            Одно нажатие — ключ Nextcloud создастся сам (окна и подтверждения не нужны).
          </p>
          <Divider plain style={{ margin: '12px 0 8px', fontSize: 12 }}>запасные варианты</Divider>
          <Button type="default" loading={flowPolling} onClick={startDeviceFlow}>
            Через SSO-окно (вручную)
          </Button>
          {flowOpen && !flowDone && (
            <p style={{ marginTop: 16, fontSize: 13, color: '#94a3b8' }}>
              В открывшемся окне вход выполнится автоматически (через корпоративный SSO).
              Останется нажать <b>«Разрешить доступ»</b> — и всё, больше подключать не придётся.
              {flowPolling && ' Ожидаем подтверждение…'}
              <br />
              <span style={{ fontSize: 12 }}>
                Окно не открылось? Разрешите всплывающие окна и нажмите кнопку ещё раз.
              </span>
            </p>
          )}
          <Divider plain style={{ margin: '16px 0 8px' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Не получается? Ключ можно создать вручную: откройте{' '}
            <a href="https://opp.spzvg.ru/settings/user/security" target="_blank" rel="noreferrer">
              opp.spzvg.ru → Настройки → Безопасность
            </a>{' '}
            (вход через корпоративный аккаунт) → «Devices & sessions» → введите имя
            «ТЕРРА-СУП» → скопируйте пароль приложения и вставьте здесь:
          </Text>
          <ManualKeyForm onSaved={async () => {
            setNotConfigured(false);
            setNeedsReconnect(false);
            await loadRooms();
          }} />
        </Card>
      </div>
    );
  }

  // ── Основной экран ─────────────────────────────────────────────────
  return (
    <div className="page-container" style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ marginBottom: 12 }}>
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CloudOutlined style={{ color: '#0082c9' }} /> Чат
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            Nextcloud Talk · обновление ~20 с
          </Text>
        </h1>
      </div>

      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
        {/* Список комнат */}
        <Card
          size="small"
          style={{ width: 320, overflow: 'auto' }}
          styles={{ body: { padding: 0 } }}
          title={
            <span>
              Комнаты{' '}
              <Badge count={(rooms || []).reduce((s, r) => s + (r.unread_messages || 0), 0)} size="small" />
            </span>
          }
          extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => loadRooms()} />}
        >
          {rooms === null ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
          ) : roomsError ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#ef4444' }}>{roomsError}</div>
          ) : rooms.length === 0 ? (
            <Empty style={{ padding: 24 }} description="Нет доступных комнат Talk" />
          ) : (
            <List
              dataSource={[...rooms].sort((a, b) => (b.unread_messages - a.unread_messages))}
              renderItem={(room) => (
                <List.Item
                  style={{
                    cursor: 'pointer', padding: '10px 12px',
                    background: current?.token === room.token ? '#e0e7ff' : undefined,
                  }}
                  onClick={() => openRoom(room)}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge count={room.unread_messages} size="small">
                        <Avatar style={{ background: '#0082c9' }} icon={<CloudOutlined />} />
                      </Badge>
                    }
                    title={<Text strong={room.unread_messages > 0} ellipsis style={{ maxWidth: 190 }}>{room.name}</Text>}
                    description={
                      <Text type="secondary" ellipsis style={{ maxWidth: 190, fontSize: 12 }}>
                        {room.last_message ? `${room.last_message_actor}: ${room.last_message}` : '—'}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* Открытая комната */}
        <Card
          size="small"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
          title={
            current ? (
              <span>
                {!current.unread && null}
                {current.name}
              </span>
            ) : 'Выберите комнату'
          }
          extra={
            current && (
              <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setCurrent(null)}>
                К комнатам
              </Button>
            )
          }
        >
          {!current ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty description="Выберите комнату слева" />
            </div>
          ) : (
            <>
              <div ref={msgListRef} style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : messages.length === 0 ? (
                  <Empty description="Нет сообщений" />
                ) : (
                  messages.map((m) => {
                    const own = me && m.actor_id === me;
                    if (m.system_message) {
                      return (
                        <div key={String(m.id)} style={{ textAlign: 'center', margin: '6px 0' }}>
                          <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                            {renderTalkText(m)}
                          </Text>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={String(m.id)}
                        style={{ display: 'flex', justifyContent: own ? 'flex-end' : 'flex-start', marginBottom: 10 }}
                      >
                        {!own && (
                          <Avatar size="small" style={{ background: '#0082c9', marginRight: 8, flexShrink: 0 }} icon={<UserOutlined />} />
                        )}
                        <div style={{
                          maxWidth: '72%',
                          background: own ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : '#f1f5f9',
                          color: own ? '#fff' : undefined,
                          borderRadius: 12,
                          padding: '8px 12px',
                        }}>
                          <div style={{ fontSize: 12, marginBottom: 2 }}>
                            <Text strong style={{ fontSize: 12, color: own ? '#fff' : undefined }}>
                              {own ? 'Вы' : m.actor_name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, color: own ? 'rgba(255,255,255,0.7)' : undefined }}>
                              {fmtTime(m.timestamp)}
                            </Text>
                          </div>
                          <div style={{ fontSize: 14 }}>
                            {renderTalkText(m)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Ввод */}
              <div style={{ borderTop: '1px solid #e2e8f0', padding: 10, display: 'flex', gap: 8 }}>
                <Tooltip title="Прикрепить файл (уходит в Nextcloud)">
                  <Button
                    icon={<UploadOutlined />} loading={fileUploading}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.onchange = () => input.files[0] && uploadFile(input.files[0]);
                      input.click();
                    }}
                  />
                </Tooltip>
                <Input.TextArea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Сообщение в Nextcloud Talk…"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  style={{ flex: 1 }}
                />
                <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={send} disabled={!text.trim()}>
                  Отправить
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}


/** Ручной фолбэк: вставка app-password из NC (Settings → Security). */
function ManualKeyForm({ onSaved }) {
  const [login, setLogin] = useState('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!login.trim() || !token.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('nc_username', login.trim());
      fd.append('nc_password', token.trim());
      await api.post('/exchange/credentials', fd);
      message.success('Ключ сохранён — подключаемся…');
      setLogin(''); setToken('');
      onSaved?.();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось сохранить ключ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      <Input placeholder="Логин NC (username)" value={login} onChange={(e) => setLogin(e.target.value)} style={{ width: 160 }} />
      <Input.Password placeholder="Пароль приложения" value={token} onChange={(e) => setToken(e.target.value)} onPressEnter={save} style={{ flex: 1 }} />
      <Button loading={saving} disabled={!login.trim() || !token.trim()} onClick={save}>Сохранить</Button>
    </div>
  );
}
